import React from "react";
import { getIntervalOptions } from "@/utils/calendarUtils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useFundCalendarFormModalController,
  FundCalendarFormModalProps,
} from "./useFundCalendarFormModalController";

// Calendar interval options
const INTERVAL_OPTIONS = getIntervalOptions();

export default function FundCalendarFormModal(
  props: FundCalendarFormModalProps
) {
  const controller = useFundCalendarFormModalController(props);

  return (
    <Dialog open={controller.isOpen} onOpenChange={() => controller.onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={controller.handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {controller.calendar ? "Edit" : "Create"} Fund Calendar
            </DialogTitle>
            <DialogDescription>
              {controller.calendar
                ? "Update the details of this fund calendar"
                : "Create a new fund calendar to define reporting periods"}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Name
              </Label>
              <Input
                id="name"
                name="name"
                value={controller.formData.name}
                onChange={controller.handleInputChange}
                className="col-span-3"
                placeholder="Calendar name"
                required
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="intervalDefinition" className="text-right">
                Interval Type
              </Label>
              <Select
                value={controller.formData.intervalDefinition}
                onValueChange={controller.handleIntervalChange}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select interval type" />
                </SelectTrigger>
                <SelectContent>
                  {INTERVAL_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <div className="col-span-1"></div>
              <div className="flex items-center space-x-2 col-span-3">
                <Checkbox
                  id="isDefault"
                  checked={
                    controller.isFirstCalendar
                      ? true
                      : controller.formData.isDefault
                  }
                  onCheckedChange={controller.handleCheckboxChange}
                  disabled={controller.isFirstCalendar}
                />
                <Label htmlFor="isDefault">Set as default calendar</Label>
                {controller.isFirstCalendar && (
                  <span className="text-xs text-amber-600 ml-2">
                    First calendar must be default
                  </span>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => controller.onClose()}
              disabled={controller.isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={controller.isSubmitting}>
              {controller.isSubmitting
                ? "Saving..."
                : controller.calendar
                ? "Update"
                : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
